import { api } from "@/lib/api";

// Executive Summary Response
export interface ExecutiveSummaryResponse {
  status: "success" | "error";
  message: string;
  data: {
    date: string;
    totalWashes: number;
    subscriptionsUsed: number;
    oneTimeOrders: number;
    cashCollected: number;
    cancelled: number;
    staffWorkload: Array<{
      staffId: string;
      staffName: string;
      appointments: number;
      percentage: number;
    }>;
    workloadImbalance: boolean;
  };
}

// Operations Report Response
export interface OperationsReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staffWorkload: {
      chartData: Array<{
        date: string;
        [staffName: string]: string | number;
      }>;
      topStaff: string[];
      imbalance: boolean;
      imbalanceDetails: Array<{
        name: string;
        appointments: number;
      }> | null;
    };
    dailyOperations: Array<{
      date: string;
      dateStr: string;
      scheduled: number;
      completed: number;
      cancelled: number;
      total: number;
      completionRate: number;
    }>;
  };
}

// Subscriptions Report Response
export interface SubscriptionsReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    summary: {
      activeCount: number;
      usedToday: number;
      totalRemaining: number;
      expiringSoon: number;
    };
    trend: Array<{
      date: string;
      subscriptions: number;
      oneTime: number;
      total: number;
    }>;
    comparison: Array<{
      type: string;
      count: number;
      percentage: number;
    }>;
  };
}

// Staff Performance Report Response
export interface StaffPerformanceReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    performance: Array<{
      staffId: string;
      staffName: string;
      role: string;
      completedJobs: number;
      cancelledJobs: number;
      totalJobs: number;
      completionRate: number;
      cancellationRate: number;
      revenue: number;
      status: "high" | "average" | "needs-attention";
      isLowOutput: boolean;
    }>;
    summary: {
      highPerformers: number;
      needsAttention: number;
      averagePerformers: number;
    };
  };
}

// Revenue Report Response
export interface RevenueReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    summary: {
      totalCash: number;
      avgRevenuePerWash: number;
      highestDay: {
        date: string;
        revenue: number;
      };
      lowestDay: {
        date: string;
        revenue: number;
      };
    };
    revenueVsRequests: Array<{
      date: string;
      revenue: number;
      requests: number;
    }>;
  };
}

// Losses Report Response
export interface LossesReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    summary: {
      total: number;
      totalLostRevenue: number;
      avgLostPerCancellation: number;
    };
    byReason: {
      [reason: string]: number;
    };
    staffCancellations: Array<{
      name: string;
      count: number;
    }>;
    dayWiseTrend: Array<{
      date: string;
      cancellations: number;
    }>;
    details: Array<{
      appointmentId: string;
      date: string;
      customerName: string;
      vehiclePlate: string;
      staffName: string;
      lostRevenue: number;
    }>;
  };
}

interface ReportDateRangeParams {
  startDate?: string;
  endDate?: string;
}

// Staff info for dynamic staff-based reports
export interface StaffInfo {
  id: string;
  name: string;
}

// Daily Completed Orders Report Response
export interface DailyCompletedReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    dailyData: Array<{
      date: string;
      byStaff: { [staffName: string]: number };
      total: number;
    }>;
    totals: {
      byStaff: { [staffName: string]: number };
      total: number;
    };
  };
}

// Cash Collection Report Response
export interface CashCollectionReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    dailyData: Array<{
      date: string;
      byStaff: { [staffName: string]: { orders: number; amount: number } };
      total: { orders: number; amount: number };
    }>;
    totals: {
      byStaff: { [staffName: string]: { orders: number; amount: number } };
      total: { orders: number; amount: number };
    };
  };
}

// Sales Report Response
export interface SalesReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    records: Array<{
      orderId: string;
      date: string;
      customerName: string;
      serviceType: string;
      packageType: string | null;
      assignedTo: string;
      bookingRef: string | null;
      status: string;
      price: number;
      quantity: number;
      totalAmount: number;
      paymentMode: string;
      serviceProvider: string | null;
      customerType: string;
    }>;
    totals: {
      totalOrders: number;
      totalRevenue: number;
      totalQuantity: number;
    };
  };
}

// Subscription Revenue Report Response
export interface SubscriptionRevenueReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    summary: {
      totalSubscribers: number;
      activeSubscribers: number;
      newSubscriptions: number;
      cancelledSubscriptions: number;
      netSubscribers: number;
    };
    planBreakdown: Array<{
      plan: string;
      activeCount: number;
      price: number;
      revenue: number;
    }>;
    totalRevenue: number;
  };
}

// Not Completed Orders Report Response
export interface NotCompletedOrdersReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    dailyData: Array<{
      date: string;
      byStaff: { [staffName: string]: number };
      total: number;
    }>;
    totals: {
      byStaff: { [staffName: string]: number };
      total: number;
    };
  };
}

// Outstanding Payment Report Response
export interface OutstandingPaymentReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    records: Array<{
      customerId: string;
      month: string;
      customerName: string;
      invoiceAmount: number;
      paymentAmount: number | null;
      adjustment: number;
      status: "paid" | "unpaid";
      remarks: string | null;
    }>;
    totals: {
      invoice: number;
      payment: number;
      adjustment: number;
    };
  };
}

// Subscription Usage Report Response
export interface SubscriptionUsageReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    customers: Array<{
      customerId: string;
      customerName: string;
      byStaff: { [staffName: string]: { outside: number; inside: number } };
      totalOutside: number;
      totalInside: number;
    }>;
    totals: {
      byStaff: { [staffName: string]: { outside: number; inside: number } };
      totalOutside: number;
      totalInside: number;
    };
  };
}

// Monthly Summary Report Response
export interface MonthlySummaryReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    summary: Array<{
      status: string;
      byStaff: { [staffName: string]: { count: number; percentage: number } };
      total: { count: number; percentage: number };
    }>;
    grandTotal: number;
  };
}

// Customer Wash History Report Response
export interface CustomerWashHistoryReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    customerId: string;
    customerName: string;
    dateRange: {
      start: string;
      end: string;
    };
    history: Array<{
      cleanDate: string;
      serviceType: string;
      assignedUnit: string;
      status: string;
      washCount: number;
    }>;
    totalWashes: number;
  };
}

// Offline Booking Report Response
export interface OfflineBookingReportResponse {
  status: "success" | "error";
  message: string;
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    summary: {
      totalBookings: number;
      byCategory: {
        occasional: number;
        regular: number;
        loyal: number;
      };
      totalRevenue: number;
    };
    dailyData: Array<{
      date: string;
      byCategory: {
        occasional: { count: number; revenue: number };
        regular: { count: number; revenue: number };
        loyal: { count: number; revenue: number };
      };
      total: { count: number; revenue: number };
    }>;
    records: Array<{
      appointmentId: string;
      date: string;
      customerName: string;
      customerCategory: string;
      vehiclePlate: string;
      serviceType: string;
      assignedTo: string;
      status: string;
      amount: number;
    }>;
  };
}

export const reportsService = {
  /**
   * Get executive summary for a specific date
   * @param date Date in YYYY-MM-DD format (defaults to today)
   */
  async getExecutiveSummary(date?: string): Promise<ExecutiveSummaryResponse> {
    const params = date ? { date } : {};
    const response = await api.get<ExecutiveSummaryResponse>(
      "/reports/executive-summary",
      { params }
    );
    return response.data;
  },

  /**
   * Get operations report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getOperationsReport(
    params?: ReportDateRangeParams
  ): Promise<OperationsReportResponse> {
    const response = await api.get<OperationsReportResponse>(
      "/reports/operations",
      { params }
    );
    return response.data;
  },

  /**
   * Get subscriptions report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getSubscriptionsReport(
    params?: ReportDateRangeParams
  ): Promise<SubscriptionsReportResponse> {
    const response = await api.get<SubscriptionsReportResponse>(
      "/reports/subscriptions",
      { params }
    );
    return response.data;
  },

  /**
   * Get staff performance report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getStaffPerformanceReport(
    params?: ReportDateRangeParams
  ): Promise<StaffPerformanceReportResponse> {
    const response = await api.get<StaffPerformanceReportResponse>(
      "/reports/staff-performance",
      { params }
    );
    return response.data;
  },

  /**
   * Get revenue report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getRevenueReport(
    params?: ReportDateRangeParams
  ): Promise<RevenueReportResponse> {
    const response = await api.get<RevenueReportResponse>("/reports/revenue", {
      params,
    });
    return response.data;
  },

  /**
   * Get losses report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getLossesReport(
    params?: ReportDateRangeParams
  ): Promise<LossesReportResponse> {
    const response = await api.get<LossesReportResponse>("/reports/losses", {
      params,
    });
    return response.data;
  },

  /**
   * Get daily completed orders report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getDailyCompletedReport(
    params?: ReportDateRangeParams
  ): Promise<DailyCompletedReportResponse> {
    const response = await api.get<DailyCompletedReportResponse>(
      "/reports/daily-completed",
      { params }
    );
    return response.data;
  },

  /**
   * Get cash collection report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getCashCollectionReport(
    params?: ReportDateRangeParams
  ): Promise<CashCollectionReportResponse> {
    const response = await api.get<CashCollectionReportResponse>(
      "/reports/cash-collection",
      { params }
    );
    return response.data;
  },

  /**
   * Get sales report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getSalesReport(
    params?: ReportDateRangeParams
  ): Promise<SalesReportResponse> {
    const response = await api.get<SalesReportResponse>("/reports/sales", {
      params,
    });
    return response.data;
  },

  /**
   * Get subscription revenue report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getSubscriptionRevenueReport(
    params?: ReportDateRangeParams
  ): Promise<SubscriptionRevenueReportResponse> {
    const response = await api.get<SubscriptionRevenueReportResponse>(
      "/reports/subscription-revenue",
      { params }
    );
    return response.data;
  },

  /**
   * Get not completed orders report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getNotCompletedOrdersReport(
    params?: ReportDateRangeParams
  ): Promise<NotCompletedOrdersReportResponse> {
    const response = await api.get<NotCompletedOrdersReportResponse>(
      "/reports/not-completed-orders",
      { params }
    );
    return response.data;
  },

  /**
   * Get outstanding payment report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getOutstandingPaymentReport(
    params?: ReportDateRangeParams
  ): Promise<OutstandingPaymentReportResponse> {
    const response = await api.get<OutstandingPaymentReportResponse>(
      "/reports/outstanding-payments",
      { params }
    );
    return response.data;
  },

  /**
   * Get subscription usage report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getSubscriptionUsageReport(
    params?: ReportDateRangeParams
  ): Promise<SubscriptionUsageReportResponse> {
    const response = await api.get<SubscriptionUsageReportResponse>(
      "/reports/subscription-usage",
      { params }
    );
    return response.data;
  },

  /**
   * Get monthly summary report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getMonthlySummaryReport(
    params?: ReportDateRangeParams
  ): Promise<MonthlySummaryReportResponse> {
    const response = await api.get<MonthlySummaryReportResponse>(
      "/reports/monthly-summary",
      { params }
    );
    return response.data;
  },

  /**
   * Get customer wash history report
   * @param customerId Customer ID to get history for
   * @param params Date range parameters
   */
  async getCustomerWashHistoryReport(
    customerId: string,
    params?: ReportDateRangeParams
  ): Promise<CustomerWashHistoryReportResponse> {
    const response = await api.get<CustomerWashHistoryReportResponse>(
      "/reports/customer-wash-history",
      { params: { ...params, customerId } }
    );
    return response.data;
  },

  /**
   * Get offline booking report for a date range
   * @param params Date range parameters (defaults to current month)
   */
  async getOfflineBookingReport(
    params?: ReportDateRangeParams
  ): Promise<OfflineBookingReportResponse> {
    const response = await api.get<OfflineBookingReportResponse>(
      "/reports/offline-booking",
      { params }
    );
    return response.data;
  },
};
